import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CodeMirror from "@uiw/react-codemirror";
import { AxiosError } from "axios";
import { format, parseISO } from "date-fns";
import { FileEdit, Pen, PencilIcon } from "lucide-react";

import {
  Award,
  BarChart,
  Calendar,
  Check,
  Clipboard,
  Clock,
  Contact2,
  Download,
  Edit2,
  File,
  FileText,
  Flag,
  Gauge,
  Group,
  Hourglass,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Search,
  Settings,
  Star,
  Trash2,
  User,
  Users,
  Users as UsersIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  GradeResponse,
  GradeSubmissionRequest,
  saveRubrics,
} from "../api/createGradeApi";
import {
  getProjectById,
  getProjectByIdExport,
  getTemplateById,
  publishProject,
  unpublishProject,
  useEvaluationScriptStatusQuery,
  useGenerateEvaluationScript,
  useGetEvaluationScriptQuery,
  useUpdateEvaluationScript,
} from "../api/projectManagementApi";
import { ResourceProfileData } from "../api/sandboxManagementApi";
import { getProjectSubmissions } from "../api/studentSubmissionsApi";
import { useDeleteUserById } from "../api/userManagementAPI";
import { useAuth, useNavigation } from "../App";
import FeedbackDialog from "../components/FeedbackDialog";
import ImportGroupDialog from "../components/ImportGroupDialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ProjectHeader } from "../components/ui/page-header";
import { Progress } from "../components/ui/progress";
import {
  DatasetFile,
  ProjectFilesSection,
} from "../components/ui/ProjectFilesSection";
import RightSubmissionPanel from "../components/ui/RightSubmissionPanel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { TabNavigation } from "../components/ui/tab-navigation";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { ROLES } from "../constant/roles";
import { downloadAsXlsx } from "../helper/helperFunctions";
import { cn } from "../lib/utils";
import paths from "../routes";

interface Student {
  id: string;
  name: string;
  email: string;
  studentId: string;
  status: string;
  checkPointSubmitted: number;
  totalCheckPoints: number;
}

interface StudentRow {
  id: string;
  name?: string;
  email?: string;
  status?: string;
  groupName?: string;
  groupCode?: string;
  groupId?: string;
}

export interface UserBasic {
  id: string;
  name: string;
  email: string;
}

export interface UserWithStatus extends UserBasic {
  status: string;
}

export interface Rubric {
  id: string;
  title: string;
  description: string;
  criteria: Criterion[];
  total_points: string;
  grading_scale: Record<string, any>;
  is_template: boolean;
  template_name: string | null;
}

interface Criterion {
  name: string;
  points: number;
  description: string;
}

export interface ProjectCourse {
  id: string;
  name: string;
  code: string;
  term: string;
  description: string;
}

interface ProjectCheckpoint {
  id: string;
  title: string;
  description: string;
  checkpoint_number: number;
  due_date: string;
  status: string | any;
  is_required: boolean;
  metadata: Record<string, any>;
  rubrics?: Rubric[];
}

interface AssignmentStats {
  totalAssignments: number;
  byRole: Record<string, number>;
  roles: string[];
}

export interface ProjectResponse {
  id: string;
  title: string;
  description: string;
  instructions: string;
  course_id: string;
  created_by: string;
  status: string;
  difficulty_level: "beginner" | "intermediate" | "advanced";
  estimated_hours: number;
  notebook_template_s3_url: string | null;
  dataset_s3_url: DatasetFile[];
  additional_files_s3_urls: DatasetFile[];
  start_date: string | null;
  due_date: string | null;
  late_submission_allowed: boolean;
  late_penalty_percent: string;
  max_attempts: number;
  auto_grading_enabled: boolean;
  evaluation_type:
    | "test_script"
    | "exact_match"
    | "ai_assisted"
    | "evaluation_script"
    | null;
  learning_objectives: string | null;
  prerequisites: string | null;
  tags: string[];
  settings: Record<string, any>;
  isScreen: number;
  project_code: string;
  category_id: string;
  instructor_id: string[];
  teaching_ass_id: string[];
  total_points: number;
  project_overview: string | null;
  type: "individual" | "group";
  sandbox_time_duration: string | null;
  late_submission_days_allowed: number;
  published_by: string | null;
  published_at: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  course: ProjectCourse;
  creator: UserBasic;
  rubrics: Rubric[];
  checkpoints: ProjectCheckpoint[];
  template: any;
  assignments: any[];
  assignmentStats: AssignmentStats;
  instructors: UserWithStatus[];
  teachingAssistants: UserWithStatus[];
  students: UserWithStatus[];
  Submission: {
    count: number;
    rows: any[];
  };
}

// Helper function to get checkpoint status explanation
const getCheckpointStatusExplanation = (status: string): string => {
  const statusMap: Record<string, string> = {
    "Pending Submission": "No students have any progress on this checkpoint",
    Completed: "All students have been graded for this checkpoint",
    Graded: "Some students graded, some pending for this checkpoint",
    Submitted: "Some students submitted this checkpoint but none graded yet",
    "In Progress": "Some students are working on this checkpoint",
  };

  return statusMap[status] || "Status information not available";
};

// Helper function to get status styling
const getCheckpointStatusStyle = (status: string): string => {
  const styleMap: Record<string, string> = {
    "Pending Submission":
      "text-sm bg-gray-400 p-1 px-2 rounded-md text-white font-semibold",
    Completed:
      "text-sm bg-green-500 p-1 px-2 rounded-md text-white font-semibold",
    Graded:
      "text-sm bg-yellow-500 p-1 px-2 rounded-md text-white font-semibold",
    Submitted:
      "text-sm bg-blue-500 p-1 px-2 rounded-md text-white font-semibold",
    "In Progress":
      "text-sm bg-orange-500 p-1 px-2 rounded-md text-white font-semibold",
  };

  return styleMap[status] || "text-inherit";
};

type InstructorProjectDetailsProps = {
  projectId?: string;
  isProjectPreview?: boolean;
  setIsProjectPreview?: React.Dispatch<React.SetStateAction<boolean>>;
};

// Helper function to calculate total students in group projects
const calculateTotalStudents = (project: any, submissionsData: any) => {
  if (!project) return 0;

  if (project.type === "group") {
    // For group projects, check if students have the nested group structure from getProjectDetails API
    if (project.students && Array.isArray(project.students)) {
      // Check if students array contains groups with members (getProjectDetails structure)
      if (
        project.students.length > 0 &&
        project.students[0]?.groupId &&
        project.students[0]?.members
      ) {
        const uniqueStudents = new Set();

        project.students.forEach((group: any) => {
          if (group.members && Array.isArray(group.members)) {
            group.members.forEach((member: any) => {
              uniqueStudents.add(member.id);
            });
          }
        });

        return uniqueStudents.size;
      }

      // Fallback: check for Group Configuration structure (different API response)
      if (project.students.some((student: any) => student.groupId)) {
        const uniqueStudents = new Set();

        project.students
          .filter((group: any) => group.groupId)
          .forEach((group: any) => {
            if (group.members && Array.isArray(group.members)) {
              group.members.forEach((member: any) => {
                uniqueStudents.add(member.id);
              });
            }
          });

        return uniqueStudents.size;
      }
    }

    // Use submission data if available and structure is different
    if (submissionsData?.totalStudents > 0) {
      return submissionsData.totalStudents;
    }

    // Final fallback for group projects
    return project.students?.length || 0;
  }

  // For individual projects, return the direct count
  return project.students?.length || 0;
};

// Helper function to calculate total groups in group projects
const calculateGroupCount = (project: any, submissionsData: any) => {
  if (!project || project.type !== "group") return 0;

  // Check if students array contains groups with members (getProjectDetails structure)
  if (project.students && Array.isArray(project.students)) {
    if (
      project.students.length > 0 &&
      project.students[0]?.groupId &&
      project.students[0]?.members
    ) {
      // Count unique groups
      const uniqueGroups = new Set();

      project.students.forEach((group: any) => {
        if (group.groupId) {
          uniqueGroups.add(group.groupId);
        }
      });

      return uniqueGroups.size;
    }

    // Fallback: check for Group Configuration structure
    if (project.students.some((student: any) => student.groupId)) {
      const uniqueGroups = new Set();

      project.students
        .filter((group: any) => group.groupId)
        .forEach((group: any) => {
          if (group.groupId) {
            uniqueGroups.add(group.groupId);
          }
        });

      return uniqueGroups.size;
    }
  }

  // Use submission data if available
  if (submissionsData?.studentSubmissions) {
    // Each submission represents a group in group projects
    return submissionsData.studentSubmissions.length;
  }

  return 0;
};

function mapSubmissionsApiResponse(apiResponse: any) {
  if (!apiResponse?.data) {
    return {
      totalStudents: 0,
      numberSubmitted: 0,
      numberGraded: 0,
      numberYetToGrade: 0,
      studentSubmissions: [],
    };
  }

  const { submissions = [], statistics = {}, project = {} } = apiResponse.data;
  const totalSubmissions = statistics.total_submission_count || 0;
  const gradedCount = statistics.graded_count || 0;
  const gradingCount = statistics.grading_count || 0;
  const isGroupProject = project.isGroupProject;

  // Calculate total students based on project type
  let totalStudents = totalSubmissions;
  let submittedStudents = totalSubmissions;

  if (isGroupProject) {
    // For group projects, count individual students within groups
    totalStudents = submissions.reduce((total: number, submission: any) => {
      if (submission.group?.members) {
        return total + submission.group.members.length;
      }
      return total + 1; // fallback if no group members info
    }, 0);

    submittedStudents = totalStudents; // All students in submitted groups
  }

  const studentSubmissions = submissions.map((submission: any) => {
    let latestSubmissionId = submission.id || submission.submission_id || "";
    let latestSubmittedAt = submission.submittedAt;

    if (submission.submissions && Array.isArray(submission.submissions)) {
      submission.submissions.forEach((sub: any) => {
        const subDate = new Date(sub.submittedAt).getTime();
        const latestDate = new Date(latestSubmittedAt).getTime();

        if (subDate > latestDate) {
          latestSubmittedAt = sub.submittedAt;
          latestSubmissionId = sub.id || sub.submission_id || "";
        }
      });
    }

    return {
      members: [
        {
          userId: submission.user.id,
          name: submission.user.name,
          id: submission.user.id,
          email: submission.user.email,
        },
      ],
      lastSubmissionDate: submission.latest_checkpoint_submitted_at,
      lastSubmissionStatus: submission.status,
      lastSubmittedCheckpointName: submission.latest_checkpoint_name || "N/A",
      lastSubmissionId: latestSubmissionId,
      submissionData: submission,
    };
  });

  return {
    totalStudents,
    numberSubmitted: submittedStudents,
    numberGraded: gradedCount,
    numberYetToGrade: gradingCount,
    studentSubmissions,
  };
}

export default function InstructorProjectDetailsPage({
  isProjectPreview,
  setIsProjectPreview,
}: InstructorProjectDetailsProps) {
  const { projectId, userId: urlUserId } = useParams<{
    projectId?: string;
    userId?: string;
  }>();
  const { navigateTo, goBack } = useNavigation();
  const { user } = useAuth();
  const location = useLocation() as any;
  const [searchTerm, setSearchTerm] = useState("");
  const [publishButton, setPublishButton] = useState("Draft");
  const [statusFilter, setStatusFilter] = useState("all");
  const [checkpointsFilter, setCheckpointsFilter] = useState("all");
  const [localScriptContent, setLocalScriptContent] = useState<string | null>(
    null,
  );
  const [isEditingScript, setIsEditingScript] = useState<boolean>(false);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);

  // AlertDialog state for Save Script / Update Script Confirmation
  const [confirmModalState, setConfirmModalState] = useState<{
    isOpen: boolean;
    type: "save" | "generate" | null;
  }>({
    isOpen: false,
    type: null,
  });

  const [activeTab, setActiveTab] = useState<
    "overview" | "submissions" | "students" | "evaluation"
  >(() => {
    const returnTab = sessionStorage.getItem("project_return_tab");
    if (
      returnTab &&
      ["overview", "submissions", "students", "evaluation"].includes(returnTab)
    ) {
      return returnTab as any;
    }
    return "overview";
  });

  useEffect(() => {
    const returnTab = sessionStorage.getItem("project_return_tab");
    if (
      returnTab &&
      ["overview", "submissions", "students", "evaluation"].includes(returnTab)
    ) {
      sessionStorage.removeItem("project_return_tab");
      setActiveTab(returnTab as any);
    }
  }, [location.pathname]);
  const [isPublished, setIsPublished] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState<
    | {
        userId: string;
        name: string;
      }[]
    | null
  >(null);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [currentSubmissionId, setCurrentSubmissionId] = useState<string>("");
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<
    string | null
  >(null);
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
  const skipNextUrlProcessing = useRef(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isImportGroupDialogOpen, setIsImportGroupDialogOpen] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const action = searchParams.get("action");

  const handleFeedbackSubmit = async (
    payloadOrBody: GradeResponse | GradeSubmissionRequest | any,
    opts?: { source?: string },
  ) => {
    try {
      if (opts?.source === "dialog") {
        const gradeResp: GradeResponse = payloadOrBody as GradeResponse;

        if (currentSubmissionId) {
          queryClient.invalidateQueries({
            queryKey: ["progressDetails", currentSubmissionId],
          });
        }

        // Refetch project data in the background without showing loading state
        await queryClient.refetchQueries({
          queryKey: ["project", projectId],
          type: "active",
        });

        // Refetch detailed student submission info for the Right Panel
        if (urlUserId) {
          queryClient.invalidateQueries({
            queryKey: ["studentSubmissionDetails", projectId, urlUserId],
          });
        }

        // Increment refreshKey to force UI components to remount/update
        setRefreshKey((prev) => prev + 1);

        return;
      }

      const response: GradeResponse = await saveRubrics(
        payloadOrBody as GradeSubmissionRequest,
      );

      toast.success(response.message || "Feedback submitted successfully!");

      if (currentSubmissionId) {
        queryClient.invalidateQueries({
          queryKey: ["progressDetails", currentSubmissionId],
        });
      }

      // Refetch project data in the background without showing loading state
      await queryClient.refetchQueries({
        queryKey: ["project", projectId],
        type: "active",
      });

      // Refetch project submissions to refresh the stats
      await queryClient.refetchQueries({
        queryKey: [projectId],
        type: "active",
      });

      // Refetch detailed student submission info for the Right Panel
      if (urlUserId) {
        queryClient.invalidateQueries({
          queryKey: ["studentSubmissionDetails", projectId, urlUserId],
        });
      }

      // Increment refreshKey to force UI components to remount/update
      setRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      console.error("Error saving feedback:", error);
      const errorMessage = getErrorMessage(
        error,
        "Failed to submit feedback. Please try again.",
      );
      toast.error(errorMessage);
    }
  };

  const createPublishMutation = (queryClient: any, toast: any) => {
    return useMutation({
      mutationFn: (id: string) => publishProject(id),
      onSuccess: (response, id) => {
        queryClient.setQueryData(["project", id], (oldData: any) => ({
          ...oldData,
          publicationStatus: "published",
          status: "Active",
        }));

        const successMessage =
          response?.message || "Project published successfully";
        toast.success(successMessage);
      },
      onError: (error: unknown) => {
        const errorMessage = getErrorMessage(
          error,
          "Failed to publish project",
        );
        toast.error(errorMessage);
      },
    });
  };

  const createUnpublishMutation = (queryClient: any, toast: any) => {
    return useMutation({
      mutationFn: (id: string) => unpublishProject(id),
      onSuccess: (response, id) => {
        queryClient.setQueryData(["project", id], (oldData: any) => ({
          ...oldData,
          publicationStatus: "unpublished",
          status: "draft",
        }));

        const successMessage =
          response?.message || "Project unpublished successfully";
        toast.success(successMessage);
      },
      onError: (error: unknown) => {
        const errorMessage = getErrorMessage(
          error,
          "Failed to unpublish project",
        );
        toast.error(errorMessage);
      },
    });
  };

  // Centralized error handling
  const getErrorMessage = (error: unknown, defaultMessage: string): string => {
    if (error instanceof AxiosError) {
      // Try to get message from various possible response structures
      const response = error.response;
      if (response?.data) {
        // Check response.data.data.message (your API structure)
        if (response.data?.data?.message) {
          return response.data.data.message;
        }
        // Check response.data.message
        if (response.data?.message) {
          return response.data.message;
        }
      }
      // Check error.message
      if (error.message) {
        return error.message;
      }
    }
    if (error instanceof Error) {
      return error.message || defaultMessage;
    }
    return defaultMessage;
  };

  const {
    data: rawSubmissionsResp,
    isLoading: submissionsLoading,
    error: submissionsError,
    refetch: refetchSubmissions,
  } = useQuery({
    queryKey: [projectId],
    queryFn: () => getProjectSubmissions(projectId!),
    enabled: !!projectId,
    retry: false,
  });

  // Console log the raw API response

  const submissionsData = rawSubmissionsResp
    ? mapSubmissionsApiResponse(rawSubmissionsResp)
    : {
        totalStudents: 0,
        numberSubmitted: 0,
        numberGraded: 0,
        numberYetToGrade: 0,
        studentSubmissions: [],
      };

  // Console log the processed submissions data

  const formatMaybeDate = (value?: string) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "-" : d.toLocaleString();
  };

  const {
    data: apiProject,
    isLoading: projectLoading,
    error: projectError,
  } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProjectById(projectId!),
    enabled: !!projectId && mode !== "template",
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const {
    data: apiProject2,
    isLoading: projectLoading2,
    error: projectError2,
  } = useQuery({
    queryKey: ["template", projectId],
    queryFn: () => getTemplateById(projectId!),
    enabled: !!projectId && mode === "template",
    retry: false,
  });

  // Resource profile is already included in the project data from backend
  const resourceProfile: ResourceProfileData | undefined =
    (apiProject?.resourceProfile as ResourceProfileData) ||
    (apiProject2?.resourceProfile as ResourceProfileData);
  const resourceProfileLoading2 = false;
  const resourceProfileError2 = null;
  const generateScriptMutation = useGenerateEvaluationScript();
  const updateScriptMutation = useUpdateEvaluationScript();

  const {
    data: evaluationScript,
    isLoading: scriptLoading,
    error: scriptError,
    refetch: refetchScript,
  } = useGetEvaluationScriptQuery(projectId || "", {
    enabled: !!projectId && activeTab === "evaluation",
  });

  const { data: pollingData } = useEvaluationScriptStatusQuery(
    projectId || "",
    {
      enabled: !!projectId && activeTab === "evaluation",
      refetchInterval: (query: any) => {
        // Short-poll every 2 seconds if active background job is running
        return query?.state?.data?.status === "generating" ? 2000 : false;
      },
    },
  );

  // Intercept polling mutations to notify the user seamlessly within UI context
  const previousPollingStatus = useRef<string | null>(null);
  useEffect(() => {
    if (!pollingData) return;
    const currentStatus = pollingData.status;
    const prevStatus = previousPollingStatus.current;

    // Only trigger notifications heavily on the edge flip from 'generating' -> 'completed/failed/empty'
    if (prevStatus === "generating" && currentStatus !== "generating") {
      if (currentStatus === "completed") {
        toast.success("AI Script generation completed successfully!");
        refetchScript();
      } else if (currentStatus === "failed") {
        toast.error(pollingData.message || "AI Script generation failed.");
      } else if (currentStatus === "empty") {
        toast.error(
          "Generation finished, but the AI returned an empty script. Please check instructions/rubrics.",
        );
      }
    }

    previousPollingStatus.current = currentStatus;
  }, [pollingData?.status, refetchScript]);

  // Sync network state into editor state upon load or background generation refresh
  useEffect(() => {
    if (evaluationScript !== undefined) {
      const resolvedScript =
        typeof evaluationScript === "string" ? evaluationScript : "";
      setLocalScriptContent(resolvedScript);
    }
  }, [evaluationScript]);

  useEffect(() => {
    if (apiProject?.status) {
      setPublishButton(apiProject?.status);
    }
    if (apiProject2?.status) {
      setPublishButton(apiProject2?.status);
    }
  }, [apiProject?.status, apiProject2?.status]);

  const project =
    mode === "template"
      ? ((apiProject2 as any)?.project as any)
      : (apiProject as unknown as ProjectResponse);

  const handleCheckPoint = (id: string) => {
    setActiveTab("submissions");
    setCheckpointsFilter(id);
    setStatusFilter("all");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Submitted":
        return "!text-bits-blue-500";
      case "In Progress":
        return "!text-bits-green-success-500";
      case "Late":
        return "!text-bits-error";
      default:
        return "!text-muted-foreground";
    }
  };

  // Poll every 10 s while any submission is being AI-evaluated
  const hasAnyGrading =
    submissionsData?.studentSubmissions?.some(
      (s) => s.lastSubmissionStatus === "ai_grading",
    ) ?? false;

  useEffect(() => {
    if (!hasAnyGrading) return;
    const intervalId = setInterval(() => {
      refetchSubmissions();
    }, 10000);
    return () => clearInterval(intervalId);
  }, [hasAnyGrading, refetchSubmissions]);

  const uniqueStatuses = useMemo(() => {
    const statuses = new Set<string>();
    submissionsData?.studentSubmissions?.forEach((sub) => {
      if (sub.lastSubmissionStatus) {
        statuses.add(sub.lastSubmissionStatus);
      }
    });
    return Array.from(statuses);
  }, [submissionsData]);

  useEffect(() => {
    if (skipNextUrlProcessing.current) {
      skipNextUrlProcessing.current = false;
      return;
    }

    if (
      urlUserId &&
      submissionsData?.studentSubmissions &&
      !hasInitializedFromUrl
    ) {
      setActiveTab("submissions");

      const targetSubmission = submissionsData.studentSubmissions.find((sub) =>
        sub.members?.some((m) => {
          return m.userId === urlUserId;
        }),
      );

      if (targetSubmission?.members?.[0]) {
        setSelectedStudents([
          {
            userId: targetSubmission.members[0].userId,
            name: targetSubmission.members[0].name,
          },
        ]);
        setSelectedSubmissionId(targetSubmission.lastSubmissionId);
      }

      setHasInitializedFromUrl(true);
    }
  }, [urlUserId, submissionsData]);
  const handleExportStudentDetail = async () => {
    try {
      const params: any = { isExport: true };
      params.id = projectId;
      const responseData = await getProjectByIdExport(params);

      await downloadAsXlsx(
        responseData,
        `${project.title}${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.success("Students Export Successfull");
    } catch (err: any) {
      toast.error("Student Export Failed");
    }
  };

  const filteredSubmissions = useMemo(() => {
    let filtered = submissionsData?.studentSubmissions || [];

    if (searchTerm) {
      filtered = filtered.filter((submission) =>
        submission.members.some((member) =>
          member.name.toLowerCase().includes(searchTerm.toLowerCase()),
        ),
      );
    }

    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter(
        (submission) => submission.lastSubmissionStatus === statusFilter,
      );
    }

    if (checkpointsFilter && checkpointsFilter !== "all") {
      filtered = filtered.filter(
        (submission) =>
          submission.lastSubmittedCheckpointName === checkpointsFilter ||
          (project?.checkpoints || []).find((cp) => cp.id === checkpointsFilter)
            ?.title === submission.lastSubmittedCheckpointName,
      );
    }
    return filtered;
  }, [
    submissionsData,
    searchTerm,
    statusFilter,
    checkpointsFilter,
    project?.checkpoints,
  ]);

  function getSubmissionStatus(status: string) {
    let count = 0;
    if (project.Submission?.rows && Array.isArray(project.Submission.rows)) {
      project.Submission.rows.forEach((submission) => {
        if (
          submission.status?.toLowerCase().trim() ===
          status?.toLowerCase().trim()
        ) {
          count++;
        }
      });
    }
    return count;
  }

  // Helper function to calculate student progress based on checkpoint grading
  const calculateStudentProgress = (studentId: string) => {
    const totalCheckpoints = project?.checkpoints?.length || 0;

    if (totalCheckpoints === 0) {
      return {
        completedCheckpoints: 0,
        totalCheckpoints: 0,
        percentage: 0,
      };
    }

    // Check if we have submissions data from the submissions API
    const hasSubmissionsApiData =
      (rawSubmissionsResp as any)?.data?.submissions &&
      Array.isArray((rawSubmissionsResp as any).data.submissions);

    if (hasSubmissionsApiData) {
      // NEW: Use submissions API data when available

      const rawSubmissions = (rawSubmissionsResp as any).data.submissions;
      const isGroupProject =
        (rawSubmissionsResp as any).data.project?.isGroupProject ||
        project?.type === "group";

      let studentRawSubmission;

      if (isGroupProject) {
        // For group projects, find submission where student is a group member
        studentRawSubmission = rawSubmissions.find((submission: any) => {
          if (submission.group?.members) {
            return submission.group.members.some(
              (member: any) => member.id === studentId,
            );
          }
          return false;
        });
      } else {
        // For individual projects, find submission by user ID
        studentRawSubmission = rawSubmissions.find(
          (submission: any) => submission.user?.id === studentId,
        );
      }

      if (!studentRawSubmission) {
        return {
          completedCheckpoints: 0,
          totalCheckpoints,
          percentage: 0,
        };
      }

      // Calculate progress based on submission status and grade
      let completedCheckpoints = 0;

      if (
        studentRawSubmission.grade &&
        studentRawSubmission.status === "graded"
      ) {
        // If graded, consider it as having completed checkpoints
        completedCheckpoints = 1;
      } else if (studentRawSubmission.status === "submitted") {
        // If submitted but not graded, partial progress
        completedCheckpoints = 0.5;
      }

      const percentage =
        totalCheckpoints > 0
          ? (completedCheckpoints / totalCheckpoints) * 100
          : 0;

      return {
        completedCheckpoints: Math.floor(completedCheckpoints),
        totalCheckpoints,
        percentage: Math.round(percentage),
      };
    } else {
      const projectSubmissions = project?.Submission?.rows || [];

      // Find the student's submission from project Submission data
      let studentSubmissionData = projectSubmissions.find(
        (submission: any) => submission.user_id === studentId,
      );

      // If no submission found, return 0 progress
      if (!studentSubmissionData) {
        return {
          completedCheckpoints: 0,
          totalCheckpoints,
          percentage: 0,
        };
      }

      // Count how many checkpoints have been graded by checking if rubric_scores exists for that checkpoint
      let completedCheckpoints = 0;

      if (studentSubmissionData.grade?.rubric_scores) {
        // rubric_scores is an object with checkpoint IDs as keys
        // If a checkpoint ID exists in rubric_scores, it means that checkpoint has been graded
        const rubricScores = studentSubmissionData.grade.rubric_scores;
        completedCheckpoints = Object.keys(rubricScores).length;
      }

      const percentage =
        totalCheckpoints > 0
          ? (completedCheckpoints / totalCheckpoints) * 100
          : 0;

      return {
        completedCheckpoints,
        totalCheckpoints,
        percentage: Math.round(percentage),
      };
    }
  };

  interface ApiProjectActionsProps {
    projectId: string;
    apiProject: { status: string; publicationStatus?: string } | undefined;
    publishMutation: ReturnType<typeof createPublishMutation>;
    unpublishMutation: ReturnType<typeof createUnpublishMutation>;
    publishButtonText?: string;
    project?: ProjectResponse;
    onImportGroupClick?: () => void;
    mode?: string | null;
    action?: string | null;
    navigateTo?: (path: string) => void;
  }

  function ApiProjectActions({
    projectId,
    apiProject,
    publishMutation,
    unpublishMutation,
    publishButtonText,
    project,
    onImportGroupClick,
    mode,
    action,
    navigateTo,
  }: ApiProjectActionsProps) {
    const handlePublishToggle = () => {
      if (!apiProject) return;

      if (
        apiProject.publicationStatus === "draft" ||
        apiProject.publicationStatus === "published"
      ) {
        unpublishMutation.mutate(projectId!);
      } else {
        publishMutation.mutate(projectId);
      }
    };

    // Loading state
    const isLoading = publishMutation.isPending || unpublishMutation.isPending;

    // Check if project can be published/unpublished
    const isPublishable = ["published", "draft"].includes(
      apiProject?.publicationStatus ?? "",
    );

    return (
      <div className="flex items-center gap-2">
        {mode !== "template" && !(apiProject as any)?.closed_project && (
          <Button
            className="bg-bits-blue text-white"
            onClick={() =>
              ["published"].includes(apiProject?.publicationStatus ?? "")
                ? unpublishMutation.mutate(projectId!)
                : publishMutation.mutate(projectId!)
            }
            disabled={publishMutation.isPending || unpublishMutation.isPending}
          >
            {publishMutation.isPending || unpublishMutation.isPending
              ? "Processing..."
              : ["published"].includes(apiProject?.publicationStatus ?? "")
                ? "Unpublish"
                : "Publish"}
          </Button>
        )}
        {!["published"].includes(apiProject?.publicationStatus ?? "") &&
          apiProject?.status !== "closed" && (
            <Button
              variant="outline"
              onClick={() => {
                if (projectId) {
                  const editUrl =
                    mode === "template"
                      ? `/edit-project/${projectId}?mode=template&action=edit`
                      : `/edit-project/${projectId}?mode=edit`;
                  navigate(editUrl);
                }
              }}
            >
              <Pencil className="h-4 w-4 mr-2" />
              {mode === "template" && action === "edit"
                ? "Update Template"
                : mode === "template"
                  ? "Edit Template"
                  : action === "edit"
                    ? "Update Project"
                    : "Edit Project"}
            </Button>
          )}
        {project?.type === "group" && (
          <Button
            className="bg-bits-blue text-white"
            onClick={onImportGroupClick}
          >
            <UsersIcon className="h-4 w-4 mr-2" />
            Import Group
          </Button>
        )}
      </div>
    );
  }

  const publishMutation = createPublishMutation(queryClient, toast);
  const unpublishMutation = createUnpublishMutation(queryClient, toast);
  const deleteUserMutation = useDeleteUserById();
  const handleDeleteStudent = async (
    studentId: string,
    studentName: string,
  ) => {
    if (window.confirm(`Are you sure you want to delete ${studentName}?`)) {
      try {
        await deleteUserMutation.mutateAsync({
          userId: studentId,
          courseId: project?.course?.id,
          projectId: projectId!,
        });

        // If we reach here, the deletion was successful
        toast.success("Student deleted successfully");

        // Refetch only the project data to update the students list
        await queryClient.refetchQueries({
          queryKey: ["project", projectId],
          type: "active",
          exact: true,
        });
      } catch (error: any) {
        const errorMessage = getErrorMessage(error, "Failed to delete student");
        toast.error(errorMessage);
      }
    }
  };
  return (
    <div className={`space-y-6 h-full ${isProjectPreview ? "p-0" : "p-6"}`}>
      {projectLoading || projectLoading2 ? (
        <Card className="flex justify-center w-full items-center h-[60vh] mt-16">
          Fetching project details...
        </Card>
      ) : projectError || projectError2 ? (
        <Card className="flex flex-col justify-center w-full items-center h-[60vh] mt-16 p-8">
          <div className="text-center space-y-4">
            <div className="text-red-600 text-lg font-semibold">
              Error Loading Project
            </div>
            <div className="text-gray-600">
              {(projectError as any)?.response?.data?.data?.message ||
                (projectError2 as any)?.response?.data?.data?.message ||
                (projectError as any)?.message ||
                (projectError2 as any)?.message ||
                "Failed to load project details"}
            </div>
            <button
              onClick={() => navigate(paths.manageProjects)}
              className="mt-4 px-4 py-2 bg-bits-blue text-white rounded-md hover:bg-bits-blue/90"
            >
              Go to Project Management
            </button>
          </div>
        </Card>
      ) : (
        <>
          <ProjectHeader
            title={project?.title || "Untitled Project"}
            subtitle={`${project?.course?.code || " "} ${
              project?.course?.code ? "•" : " "
            }  ${project?.creator?.name || "No Instructor"}`}
            onBackClick={() => navigate(paths.manageProjects)}
          >
            <ApiProjectActions
              projectId={projectId!}
              apiProject={apiProject}
              publishMutation={publishMutation}
              unpublishMutation={unpublishMutation}
              publishButtonText={publishButton}
              project={project}
              onImportGroupClick={() => setIsImportGroupDialogOpen(true)}
              mode={mode}
              action={action}
              navigateTo={navigateTo}
            />
          </ProjectHeader>
          {!isProjectPreview && (
            <div className="p-0 -mt-2 -mx-2">
              <TabNavigation
                tabs={[
                  {
                    key: "overview",
                    label: "Overview",
                    icon: <FileText className="h-4 w-4" />,
                  },
                  ...(mode === "template"
                    ? []
                    : [
                        {
                          key: "submissions",
                          label: "Submissions",
                          icon: <Clipboard className="h-4 w-4" />,
                        },
                      ]),
                  ...(mode === "template"
                    ? []
                    : [
                        {
                          key: "students",
                          label: "Students",
                          icon: <Users className="h-4 w-4" />,
                        },
                        // Show Evaluation Script only for script-based evaluation flows
                        ...(project?.auto_grading_enabled &&
                        project?.evaluation_type === "evaluation_script"
                          ? [
                              {
                                key: "evaluation",
                                label: "Evaluation Script",
                                icon: <File className="h-4 w-4" />,
                              },
                            ]
                          : []),
                      ]),
                ]}
                activeTab={activeTab}
                onTabChange={(tab) => {
                  skipNextUrlProcessing.current = true;
                  setActiveTab(
                    tab as
                      | "overview"
                      | "submissions"
                      | "students"
                      | "evaluation",
                  );
                  setSelectedStudents(null);
                  setSelectedSubmissionId(null);

                  if (urlUserId) {
                    navigate(`/projects/${projectId}/submissions`, {
                      replace: true,
                    });
                    setHasInitializedFromUrl(false);
                  }
                }}
                className="mb-6 mx-2"
              />
            </div>
          )}
          {activeTab === "overview" && (
            <div className="flex gap-6">
              <div className="space-y-6 col-span-1 flex-col gap-6 justify-right w-11/12">
                <div className="lg:col-span-2 space-y-6">
                  <Card className="gap-4">
                    <CardHeader>
                      <div className="flex">
                        <div className="h-6 w-6">
                          <FileText className="h-6 w-6 text-bits-blue" />
                        </div>
                        <CardTitle className="text-lg text-bits-grey font-medium mt-0 ml-4">
                          Course Information
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent className="ml-1">
                      <p className="text-bits-blue-neutral-900 text-md font-semibold mb-1">
                        {project?.course?.name}
                      </p>
                      <p className="text-bits-grey-600 text-sm mb-1">
                        {project?.course?.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                {mode === "template" && apiProject2?.template?.category && (
                  <Card className="mb-4">
                    <CardHeader>
                      <CardTitle className="text-xl font-medium text-bits-blue-800">
                        Template Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-semibold text-bits-grey-700 mb-1">
                            Category
                          </p>
                          <p className="text-bits-grey-600">
                            {apiProject2.template.category || "-"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-bits-grey-700 mb-1">
                            Subcategory
                          </p>
                          <p className="text-bits-grey-600">
                            {apiProject2.template.subcategory || "-"}
                          </p>
                        </div>
                      </div>

                      {apiProject2.template.skills_covered &&
                        apiProject2.template.skills_covered.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold text-bits-grey-700 mb-2">
                              Skills Covered
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {apiProject2.template.skills_covered.map(
                                (skill, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-blue-50 text-bits-blue-800 text-sm rounded-full border border-blue-200"
                                  >
                                    {skill}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}

                      {apiProject2.template.technologies_used &&
                        apiProject2.template.technologies_used.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold text-bits-grey-700 mb-2">
                              Technologies Used
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {apiProject2.template.technologies_used.map(
                                (tech, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-indigo-50 text-indigo-700 text-sm rounded-full border border-indigo-200"
                                  >
                                    {tech}
                                  </span>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </CardContent>
                  </Card>
                )}
                <div className="lg:col-span-2 space-y-6">
                  <Card className="gap-4">
                    <CardHeader>
                      <div className="flex">
                        <div className="h-6 w-6">
                          <Clipboard className="h-6 w-6 text-bits-blue" />
                        </div>
                        <CardTitle className="text-lg font-medium text-bits-blue-800 mt-0 ml-4">
                          {mode === "template"
                            ? "Template Overview"
                            : "Project Overview"}
                        </CardTitle>
                      </div>
                    </CardHeader>

                    <CardContent className="ml-1">
                      <div
                        className="ml-4 text-bits-grey-600 mb-4
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                        dangerouslySetInnerHTML={{
                          __html: project?.project_overview || "-",
                        }}
                      />
                      <Card className="my-2">
                        <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                          Description
                        </CardTitle>
                        <div
                          className="ml-8 mr-4 mb-4 text-bits-grey-600 
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                          dangerouslySetInnerHTML={{
                            __html: project?.description || "-",
                          }}
                        />
                      </Card>
                      <Card className="mb-4">
                        <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                          Prerequisites & Requirements
                        </CardTitle>
                        <div
                          className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                          dangerouslySetInnerHTML={{
                            __html: project?.prerequisites || "-",
                          }}
                        />
                      </Card>
                      <Card className="mb-4">
                        <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                          Learning Objectives
                        </CardTitle>
                        <div
                          className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                          dangerouslySetInnerHTML={{
                            __html: project?.learning_objectives || "-",
                          }}
                        />
                      </Card>
                      <Card className="mb-4">
                        <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                          Instructions
                        </CardTitle>
                        <div
                          className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                          dangerouslySetInnerHTML={{
                            __html: project?.instructions || "-",
                          }}
                        />
                      </Card>

                      {project?.checkpoints?.length > 0 && (
                        <div className="lg:col-span-2 space-y-6">
                          <Card className="gap-4">
                            <CardHeader>
                              <div className="flex items-center">
                                <div className="h-6 w-6">
                                  <Flag className="h-6 w-6 text-indigo-500" />
                                </div>
                                <CardTitle className="text-lg text-bits-blue-800 font-medium mt-0 ml-4">
                                  Checkpoints
                                </CardTitle>
                              </div>

                              {(project.checkpoints || []).map((checkpoint) => (
                                <Card
                                  className="gap-4 mb-6"
                                  key={checkpoint.id}
                                >
                                  <CardHeader>
                                    <div className="flex justify-between">
                                      <CardTitle className="text-lg font-medium text-bits-blue-800 mt-0">
                                        {checkpoint.title}
                                        {checkpoint.is_required && (
                                          <span className="text-red-500 mr-1">
                                            *
                                          </span>
                                        )}
                                      </CardTitle>
                                      <CardTitle className=" font-medium text-bits-blue-800 mt-0">
                                        {(user?.role === ROLES.INSTRUCTOR ||
                                          user?.role === ROLES.ADMIN ||
                                          user?.role === ROLES.TA) && (
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span
                                                className={`
        text-sm p-1 px-2 rounded-md font-semibold 
        ${
          checkpoint?.displayStatus?.toLowerCase() === "in progress"
            ? "bg-yellow-500 text-white"
            : checkpoint?.displayStatus?.toLowerCase() === "completed"
              ? "bg-green-600 text-white"
              : checkpoint?.displayStatus?.toLowerCase() ===
                  "pending submission"
                ? "bg-gray-400 text-white"
                : "text-inherit"
        }
      `}
                                              >
                                                {checkpoint?.displayStatus}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>
                                                {checkpoint?.displayStatus?.toLowerCase() ===
                                                "in progress"
                                                  ? "Students have started working on this checkpoint."
                                                  : checkpoint?.displayStatus?.toLowerCase() ===
                                                      "completed"
                                                    ? "This checkpoint has been successfully completed by the assigned students."
                                                    : checkpoint?.displayStatus?.toLowerCase() ===
                                                        "pending submission"
                                                      ? "No submissions have been made on this checkpoint."
                                                      : "Status information unavailable."}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </CardTitle>
                                    </div>
                                  </CardHeader>
                                  <CardContent className="ml-1">
                                    <p className="text-bits-grey-600 text-md font-normal mb-4">
                                      <div
                                        className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                                        dangerouslySetInnerHTML={{
                                          __html: checkpoint.description || "-",
                                        }}
                                      />
                                    </p>
                                    <div className="grid grid-cols-4 text-center w-full gap-4 text-sm mb-6">
                                      <div className="w-full">
                                        <div className="text-bits-grey-600 flex-col-reverse flex items-center justify-center gap-x-2 font-normal text-sm capitalize">
                                          <div className="">Total Students</div>
                                          <div>
                                            {
                                              checkpoint?.submissionStats
                                                ?.totalCount
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <div className="w-full">
                                        <div className="text-bits-grey-600 flex-col-reverse flex items-center justify-center gap-x-2 font-normal text-sm capitalize">
                                          <div className="flex  justify-center">
                                            Students yet to start
                                          </div>
                                          <div>
                                            {
                                              checkpoint?.submissionStats
                                                ?.notStartedCount
                                            }
                                          </div>
                                        </div>
                                      </div>
                                      <div className="w-full">
                                        <div className="text-bits-grey-600 flex-col-reverse flex items-center justify-center gap-x-2 font-normal text-sm capitalize">
                                          <div className="">
                                            Students Graded
                                          </div>
                                          <div>
                                            {
                                              checkpoint?.submissionStats
                                                ?.gradedCount
                                            }
                                          </div>
                                        </div>
                                      </div>

                                      <div className="w-full">
                                        <div className="text-bits-grey-600 flex-col-reverse flex items-center justify-center gap-x-2 font-normal text-sm capitalize">
                                          <div className="">
                                            Pending Evaluation
                                          </div>
                                          <div>
                                            {
                                              checkpoint?.submissionStats
                                                ?.pendingEvaluationCount
                                            }
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center text-sm justify-between">
                                      <div className=" flex items-center space-x-1">
                                        <Calendar className="h-4 w-4 text-bits-grey-500" />
                                        <span className=" pl-2 text-bits-grey-600 text-sm font-semibold overflow-hidden text-ellipsis w-full">
                                          <span>
                                            {checkpoint.due_date
                                              ? format(
                                                  parseISO(checkpoint.due_date),
                                                  "dd MMM yyyy",
                                                )
                                              : "-"}
                                          </span>
                                        </span>
                                      </div>
                                      {!isProjectPreview && (
                                        <div className="flex items-center justify-between text-sm font-semibold text-primary-foreground">
                                          <Button
                                            className="bg-bits-blue  hover:bg-blue-700"
                                            onClick={() =>
                                              handleCheckPoint(checkpoint.id)
                                            }
                                          >
                                            View Checkpoint Submission
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </CardHeader>
                          </Card>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              <div className="w-4/12 space-y-6">
                <Card>
                  <CardHeader className="border-b h-16">
                    <div className="flex items-start">
                      <BarChart className="h-6 w-6 text-bits-warning mr-2" />
                      <div className="text-lg font-medium text-bits-grey">
                        Quick Stats
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-1">
                    <div className="space-y-3 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Users className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">Students</p>
                        </div>
                        <p className="text-md text-bits-grey-600">
                          {calculateTotalStudents(project, submissionsData)}
                        </p>
                      </div>
                      {project?.type === "group" && (
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex justify-between items-start">
                            <Group className="h-6 w-6 mr-3 text-bits-grey-600" />
                            <p className="text-md text-bits-grey-600">
                              Group Count
                            </p>
                          </div>
                          <p className="text-md text-bits-grey-600">
                            {calculateGroupCount(project, submissionsData)}
                          </p>
                        </div>
                      )}
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Hourglass className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">
                            Estimated Hours
                          </p>
                        </div>
                        <p className="text-md text-bits-grey-600">
                          {project?.estimated_hours || 0}
                        </p>
                      </div>
                      <div className="flex justify-between items-start mb-2 gap-x-4">
                        <div className="flex justify-between items-start">
                          <Gauge className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">
                            Difficulty
                          </p>
                        </div>
                        <p className="text-md text-bits-grey-600 capitalize">
                          {project?.difficulty_level || 0}
                        </p>
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Star className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">
                            Total Points
                          </p>
                        </div>
                        <p className="text-md text-bits-grey-600">
                          {project?.total_points || 0}
                        </p>
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Clock className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">
                            Sandbox Duration
                          </p>
                        </div>
                        <p className="text-md text-bits-grey-600">
                          {project?.sandbox_time_duration
                            ? `${project.sandbox_time_duration} hrs`
                            : "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Users className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">
                            Project Type
                          </p>
                        </div>
                        <p className="text-md text-bits-grey-600 capitalize">
                          {project?.type || "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Calendar className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">Due Date</p>
                        </div>
                        <p className="text-md text-bits-grey-600">
                          {project?.due_date
                            ? format(parseISO(project.due_date), "dd MMM yyyy")
                            : "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <Flag className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">Status</p>
                        </div>
                        {(() => {
                          const statusText = project?.closed_project
                            ? "Closed"
                            : project?.status || "N/A";
                          const shouldShowTooltip = statusText.length > 15;

                          if (shouldShowTooltip) {
                            return (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="text-md text-bits-grey-600 capitalize cursor-pointer">
                                    {statusText.substring(0, 15)}...
                                  </p>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{statusText}</p>
                                </TooltipContent>
                              </Tooltip>
                            );
                          }

                          return (
                            <p className="text-md text-bits-grey-600 capitalize">
                              {statusText}
                            </p>
                          );
                        })()}
                      </div>
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex justify-between items-start">
                          <PencilIcon className="h-6 w-6 mr-3 text-bits-grey-600" />
                          <p className="text-md text-bits-grey-600">
                            Grading Type
                          </p>
                        </div>
                        <p className="text-md text-bits-grey-600 capitalize">
                          {project?.evaluation_type === "ai_assisted"
                            ? "AI-Grading"
                            : "Manual"}
                        </p>
                      </div>
                      {project?.tags && project.tags.length > 0 && (
                        <div className="flex flex-col gap-2 mt-3">
                          <div className="min-w-0">
                            <span className="block text-md font-medium text-bits-grey-600 break-words">
                              Tags:
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {project.tags.map((tag, idx) => (
                              <div
                                key={idx}
                                className="flex border border-bits-grey-300 bg-bits-grey-50 text-bits-blue-800 rounded-lg px-3 py-1 text-sm font-medium"
                              >
                                {tag}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                {console.log("project", project)}

                <Card>
                  <ProjectFilesSection
                    files={[]}
                    datasets={project?.datasets || []}
                  />
                </Card>

                {(project?.teachingAssistants?.length > 0 ||
                  project?.instructors?.length > 0) && (
                  <Card className="gap-3">
                    <CardHeader>
                      <CardTitle className="flex text-xl items-center font-semibold border-b">
                        <Users className="h-6 w-6 mr-3 text-blue-500" />
                        TA & Instructors
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {project?.teachingAssistants?.map((teacher, index) => (
                        <div key={index} className="flex flex-col">
                          <p className="font-medium">{teacher.name}</p>
                          <p className="text-muted-foreground flex items-center space-x-1">
                            <span>TA</span>
                            <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                            <span
                              className=" overflow-hidden text-ellipsis text-wrap"
                              title={teacher?.email}
                            >
                              {teacher.email}
                            </span>
                          </p>
                        </div>
                      ))}
                      {project?.instructors?.map((teacher, index) => (
                        <div key={index} className="flex flex-col over">
                          <p className="font-medium">{teacher?.name}</p>
                          <p className="text-muted-foreground flex items-center space-x-1">
                            <span>Instructor</span>
                            <span className="h-1 w-1 rounded-full bg-muted-foreground " />
                            <span
                              className="overflow-hidden text-ellipsis text-wrap"
                              title={teacher?.email}
                            >
                              {teacher?.email}
                            </span>
                          </p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {project?.type === "group" &&
                  project?.students?.length > 0 &&
                  project?.students?.some((student) => student.groupId) && (
                    <Card className="gap-3">
                      <CardHeader>
                        <CardTitle className="flex text-xl items-center font-semibold border-b">
                          <Users className="h-6 w-6 mr-3 text-blue-500" />
                          Group Configuration
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {project.students
                          .filter((student) => student.groupId)
                          .map((group) => (
                            <div
                              key={group?.groupId}
                              className="space-y-3 pb-4 border-b last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-semibold text-lg">
                                    {group?.groupName}
                                  </p>
                                  <p className="text-sm text-muted-foreground flex items-center space-x-1">
                                    <span>{group?.groupCode}</span>
                                    <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                    <span>{group?.groupCategoryName}</span>
                                  </p>
                                </div>
                                <div className="ml-2 text-sm text-muted-foreground">
                                  {group?.currentSize}/{group?.maxSize} members
                                </div>
                              </div>

                              {group?.members?.length > 0 && (
                                <div className="space-y-2 ml-4">
                                  {group.members.map((member) => (
                                    <div
                                      key={member?.id}
                                      className="flex flex-col"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <p className="font-medium">
                                          {member?.name}
                                        </p>
                                        {member?.role === "leader" && (
                                          <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                                            Leader
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-muted-foreground text-sm flex items-center space-x-1">
                                        <span className="capitalize">
                                          {member?.role}
                                        </span>
                                        <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                                        <span
                                          className="overflow-hidden text-ellipsis text-wrap"
                                          title={member?.email}
                                        >
                                          {member?.email}
                                        </span>
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </CardContent>
                    </Card>
                  )}
                <Card>
                  <CardHeader className="border-b h-16">
                    <CardTitle className="flex items-center text-xl text-bits-blue font-semibold ">
                      <Award className="h-6 w-6 mr-2 text-bits-warning" />
                      <span className="text-lg font-medium text-bits-blue-800">
                        Grading Rubric
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="mt-0 max-h-[600px] overflow-y-auto">
                    <div className="space-y-8">
                      {(project?.checkpoints || []).map((checkpoint) => (
                        <div
                          key={checkpoint.id}
                          className="space-y-4 border-b pb-6 last:border-none"
                        >
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h2 className="text-xl font-bold text-bits-blue-800 mb-2">
                              {checkpoint.title}
                            </h2>
                            {checkpoint.description && (
                              <div
                                className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                                dangerouslySetInnerHTML={{
                                  __html: checkpoint.description || "-",
                                }}
                              />
                            )}
                          </div>

                          <div className="space-y-3 ml-4">
                            {(checkpoint.rubrics || []).map((rubric) =>
                              (rubric.criteria || []).map(
                                (criterion, index) => (
                                  <div
                                    key={`${rubric.id}-${index}`}
                                    className="flex justify-between items-start border border-gray-200 rounded-lg p-4 bg-white gap-4"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <span className="block text-md font-medium text-bits-blue-800 break-words">
                                        {criterion.name}
                                      </span>
                                      <p className="text-sm text-muted-foreground mt-1 break-words">
                                        {criterion.description}
                                      </p>
                                    </div>
                                    <div className="flex-shrink-0 text-md font-medium text-bits-blue-italic italic whitespace-nowrap">
                                      ({criterion.points} points)
                                    </div>
                                  </div>
                                ),
                              ),
                            )}

                            {(!checkpoint.rubrics ||
                              checkpoint.rubrics.length === 0) && (
                              <div className="text-center py-6 text-gray-500 italic border-2 border-dashed border-gray-200 rounded-lg">
                                No rubrics defined for this checkpoint
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {(!project?.checkpoints ||
                        project.checkpoints.length === 0) && (
                        <div className="text-center py-12 text-gray-500">
                          <p className="text-lg mb-2">
                            No checkpoints defined yet
                          </p>
                          <p className="text-sm">
                            Add checkpoints and rubrics to see the grading
                            breakdown
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Resource Profile Details - only show if resourceProfile exists */}
                {resourceProfile && (
                  <Card>
                    <CardHeader className="border-b h-16">
                      <CardTitle className="flex items-center text-xl text-bits-blue font-semibold">
                        <Settings className="h-6 w-6 mr-2 text-bits-warning" />
                        <span className="text-lg font-medium text-bits-blue-800">
                          Resource Profile Details
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="-mt-2">
                      {resourceProfileLoading2 ? (
                        <div className="text-center py-6 text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bits-blue mx-auto mb-2"></div>
                          Loading resource profile...
                        </div>
                      ) : resourceProfileError2 ? (
                        <div className="text-center py-6 text-red-500">
                          <p className="text-lg mb-2">
                            Failed to load resource profile
                          </p>
                          <p className="text-sm">
                            {(resourceProfileError2 as { message?: string })
                              ?.message ||
                              "An error occurred while fetching resource profile details"}
                          </p>
                        </div>
                      ) : typeof resourceProfile !== "string" &&
                        resourceProfile ? (
                        <div className="space-y-3 flex flex-col">
                          {resourceProfile.name && (
                            <div className="mb-4 pb-3 border-b border-bits-grey-200">
                              <h3 className="text-lg font-semibold text-bits-blue-800">
                                {resourceProfile.name}
                              </h3>
                            </div>
                          )}

                          {/* CPU */}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <span className="block text-md font-medium text-bits-blue-800 break-words">
                                CPU Cores
                              </span>
                            </div>
                            <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                              {resourceProfile.cpu_cores ?? "N/A"}
                            </div>
                          </div>

                          {/* RAM */}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <span className="block text-md font-medium text-bits-blue-800 break-words">
                                Memory (RAM)
                              </span>
                            </div>
                            <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                              {resourceProfile.ram_gb
                                ? `${resourceProfile.ram_gb} GB`
                                : "N/A"}
                            </div>
                          </div>

                          {/* Disk */}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <span className="block text-md font-medium text-bits-blue-800 break-words">
                                Disk Space
                              </span>
                            </div>
                            <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                              {resourceProfile.disk_gb
                                ? `${resourceProfile.disk_gb} GB`
                                : "N/A"}
                            </div>
                          </div>

                          {/* Status */}
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <span className="block text-md font-medium text-bits-blue-800 break-words">
                                Status
                              </span>
                            </div>
                            <div className="text-md font-medium whitespace-nowrap">
                              <span
                                className={`px-3 py-1 rounded-full text-sm ${
                                  resourceProfile.is_active
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {resourceProfile.is_active
                                  ? "Active"
                                  : "Inactive"}
                              </span>
                            </div>
                          </div>

                          {/* Pre-installed Packages */}
                          {Array.isArray(resourceProfile.packages) &&
                            resourceProfile.packages.length > 0 && (
                              <div className="flex flex-col gap-2 mt-3">
                                <div className="min-w-0">
                                  <span className="block text-md font-medium text-bits-blue-800 break-words">
                                    Pre-installed Packages
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {resourceProfile.packages.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex border border-green-200 bg-green-50 text-green-800 rounded-lg px-3 py-1 text-sm font-medium"
                                    >
                                      {item}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                          {/* Custom Packages */}
                          {(() => {
                            const customPackages =
                              resourceProfile.custom_packages;

                            const packagesArray = Array.isArray(customPackages)
                              ? customPackages.filter(
                                  (pkg) => pkg && pkg.trim().length > 0,
                                )
                              : typeof customPackages === "string"
                                ? customPackages
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter((s) => s.length > 0)
                                : [];

                            if (packagesArray.length === 0) return null;

                            return (
                              <div className="flex flex-col gap-2 mt-3">
                                <div className="min-w-0">
                                  <span className="block text-md font-medium text-bits-blue-800 break-words">
                                    Custom Packages
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {packagesArray.map((item, idx) => (
                                    <div
                                      key={idx}
                                      className="flex border border-blue-200 bg-blue-50 text-blue-800 rounded-lg px-3 py-1 text-sm font-medium"
                                    >
                                      {item}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <p className="text-lg mb-2">
                            No resource profile data available
                          </p>
                          <p className="text-sm">
                            Resource profile details will appear here once
                            loaded
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardHeader className="border-b h-16">
                    <CardTitle className="flex items-center text-xl text-bits-blue font-semibold">
                      <Settings className="h-6 w-6 mr-2 text-bits-warning" />
                      <span className="text-lg font-medium text-bits-blue-800">
                        Project Settings
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="-mt-2">
                    <div className="space-y-3 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Project Code
                          </span>
                        </div>
                        <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                          {project?.project_code || "N/A"}
                        </div>
                      </div>

                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Project Status
                          </span>
                        </div>
                        <div className="text-md font-medium whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm ${
                              project?.status === "Active"
                                ? "bg-green-100 text-green-800"
                                : project?.status === "draft"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {project?.status || "Draft"}
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Late Submission Allowed
                          </span>
                        </div>
                        <div className="text-md font-medium whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm ${
                              project?.late_submission_allowed
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {project?.late_submission_allowed ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>

                      {project?.late_submission_allowed && (
                        <>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <span className="block text-md font-medium text-bits-blue-800 break-words">
                                Late Submission Days Allowed
                              </span>
                            </div>
                            <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                              {project?.late_submission_days_allowed || "N/A"}{" "}
                              days
                            </div>
                          </div>

                          {/* <div className="flex justify-between items-start mb-2">
                            <div className="flex-1 min-w-0">
                              <span className="block text-md font-medium text-bits-blue-800 break-words">
                                Late Penalty Percent
                              </span>
                            </div>
                            <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                              {project?.late_penalty_percent || "0"}%
                            </div>
                          </div> */}
                        </>
                      )}

                      {project?.start_date && (
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <span className="block text-md font-medium text-bits-blue-800 break-words">
                              Start Date
                            </span>
                          </div>
                          <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                            {format(
                              parseISO(project.start_date),
                              "dd MMM yyyy",
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Created At
                          </span>
                        </div>
                        <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                          {project?.createdAt
                            ? format(parseISO(project.createdAt), "dd MMM yyyy")
                            : "N/A"}
                        </div>
                      </div>

                      {project?.published_at && (
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0">
                            <span className="block text-md font-medium text-bits-blue-800 break-words">
                              Published At
                            </span>
                          </div>
                          <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                            {format(
                              parseISO(project.published_at),
                              "dd MMM yyyy",
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Max Submissions
                          </span>
                        </div>
                        <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                          {project?.max_attempts || "N/A"}
                        </div>
                      </div>

                      {/* <div className="flex justify-between items-start rounded-lg p-4 bg-white gap-4">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Auto Grading Enabled
                          </span>
                        </div>
                        <div className="flex-shrink-0 text-md font-medium whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm ${project?.auto_grading_enabled
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                              }`}
                          >
                            {project?.auto_grading_enabled ? "Yes" : "No"}
                          </span>
                        </div>
                      </div> */}

                      {/* <div className="flex justify-between items-start rounded-lg p-4 bg-white gap-4">
                        <div className="flex-1 min-w-0">
                          <span className="block text-md font-medium text-bits-blue-800 break-words">
                            Project Status
                          </span>
                        </div>
                        <div className="flex-shrink-0 text-md font-medium whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-sm ${
                              project?.status === "active"
                                ? "bg-green-100 text-green-800"
                                : project?.status === "draft"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {project?.status
                              ? project.status.charAt(0).toUpperCase() +
                                project.status.slice(1)
                              : "Draft"}
                          </span>
                        </div>
                      </div> */}

                      {!project && (
                        <div className="text-center py-12 text-gray-500">
                          <p className="text-lg mb-2">
                            No project data available
                          </p>
                          <p className="text-sm">
                            Project settings will appear here once loaded
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
          {activeTab === "submissions" && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold text-bits-blue-neutral-900">
                          {project?.type === "group"
                            ? submissionsData?.totalStudents || 0
                            : (project?.students.length ?? 0)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {project?.type === "group"
                            ? "Total Students"
                            : "Total Students"}
                        </p>
                      </div>
                      <Users className="h-6 w-6 text-bits-blue" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold text-bits-blue-neutral-900">
                          {submissionsData?.numberSubmitted ?? 0}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {project?.type === "group"
                            ? "Students in Submitted Groups"
                            : "Submitted"}
                        </p>
                      </div>
                      <Check className="h-6 w-6 text-bits-blue" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold text-bits-blue-neutral-900">
                          {submissionsData?.numberGraded ?? 0}
                        </div>
                        <p className="text-sm text-muted-foreground">Graded</p>
                      </div>
                      <Award className="h-6 w-6 text-bits-blue" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold text-bits-blue-neutral-900">
                          {submissionsData?.numberSubmitted -
                            submissionsData?.numberGraded || 0}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Yet to Grade
                        </p>
                      </div>
                      <Clock className="h-6 w-6 text-bits-blue" />
                    </div>
                  </CardContent>
                </Card>

                <div className="flex col-span-4 flex-col gap-6">
                  <div className="flex flex-row gap-6 overflow-hidden">
                    <div className="flex-2 min-w-0">
                      <Card>
                        <CardContent className="p-4 gap-4">
                          <div className="flex flex-col md:flex-row gap-2">
                            <div>
                              <span className="text-lg font-medium text-bits-grey">
                                Student Submissions
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                  placeholder="Search Students..."
                                  value={searchTerm}
                                  onChange={(e) =>
                                    setSearchTerm(e.target.value)
                                  }
                                  className="pl-10 w-50"
                                />
                              </div>
                            </div>

                            <Select
                              value={statusFilter}
                              onValueChange={setStatusFilter}
                            >
                              <SelectTrigger className="w-40">
                                <SelectValue placeholder="All Status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                {uniqueStatuses.map((status) => (
                                  <SelectItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() +
                                      status.slice(1)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select
                              value={checkpointsFilter}
                              onValueChange={setCheckpointsFilter}
                            >
                              <SelectTrigger className="w-52">
                                <div className="truncate">
                                  <SelectValue placeholder="All Checkpoints">
                                    {checkpointsFilter === "all"
                                      ? "All Checkpoints"
                                      : (() => {
                                          const selectedCheckpoint = (
                                            project?.checkpoints || []
                                          ).find(
                                            (cp) => cp.id === checkpointsFilter,
                                          );
                                          return (
                                            selectedCheckpoint?.title ||
                                            "All Checkpoints"
                                          );
                                        })()}
                                  </SelectValue>
                                </div>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">
                                  All Checkpoints
                                </SelectItem>

                                {(project?.checkpoints || []).map(
                                  (checkpoint) => (
                                    <SelectItem
                                      key={checkpoint.id}
                                      value={checkpoint.id}
                                    >
                                      <span
                                        className="block truncate max-w-[200px]"
                                        title={checkpoint.title}
                                      >
                                        {checkpoint.title}
                                      </span>
                                    </SelectItem>
                                  ),
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </CardContent>

                        <CardContent>
                          {submissionsLoading ? (
                            <div className="text-center py-8 text-gray-500">
                              Loading submissions...
                            </div>
                          ) : submissionsError ? (
                            <div className="text-center py-8 text-red-500">
                              {getErrorMessage(
                                submissionsError,
                                "Failed to load submissions",
                              )}
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {filteredSubmissions.map((submission, index) => (
                                <div
                                  key={index}
                                  onClick={() => {
                                    const first = submission.members?.[0];
                                    if (first) {
                                      setSelectedStudents([
                                        {
                                          userId: first.userId,
                                          name: first.name,
                                        },
                                      ]);
                                      setSelectedSubmissionId(
                                        submission.lastSubmissionId,
                                      );
                                    } else {
                                      setSelectedStudents(null);
                                      setSelectedSubmissionId(null);
                                    }
                                  }}
                                  className={cn(
                                    "flex flex-col align-top p-4 border rounded-lg space-y-3 cursor-pointer transition-colors",
                                    selectedSubmissionId ===
                                      submission.lastSubmissionId
                                      ? "bg-blue-50 border-bits-blue border-2 shadow-md"
                                      : "hover:bg-gray-50 border-gray-200",
                                  )}
                                >
                                  <div className="flex flex-col align-top">
                                    <div className="flex flex-row">
                                      <div className="flex items-center ">
                                        <div className="w-10 h-10 bg-bits-blue rounded-full flex items-center justify-center">
                                          <User className="h-5 w-5 text-white" />
                                        </div>
                                        <div className="flex flex-col ml-3">
                                          <p className="font-semibold text-lg leading-tight text-bits-blue-neutral-900">
                                            {
                                              submission?.submissionData?.user
                                                ?.name
                                            }
                                            {submission?.submissionData?.group
                                              ?.name &&
                                              ` ( ${submission?.submissionData?.group?.name} )`}
                                          </p>
                                          <p className="text-muted-foreground leading-normal">
                                            {
                                              submission?.submissionData?.user
                                                ?.email
                                            }
                                          </p>
                                        </div>
                                      </div>
                                      <div className="ml-auto">
                                        <Badge
                                          className={`capitalize text-xs ${
                                            submission.lastSubmissionStatus ===
                                            "graded"
                                              ? "bg-green-100 text-bits-green hover:bg-green-200"
                                              : submission.lastSubmissionStatus ===
                                                  "ai_grading"
                                                ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                                : submission.lastSubmissionStatus ===
                                                    "submitted"
                                                  ? "bg-blue-100 text-bits-blue hover:bg-blue-200"
                                                  : "bg-yellow-50 text-bits-warning hover:bg-yellow-100"
                                          }`}
                                          variant="outline"
                                        >
                                          {submission.lastSubmissionStatus ===
                                          "ai_grading" ? (
                                            <span className="flex items-center gap-1">
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              AI Evaluating...
                                            </span>
                                          ) : (
                                            submission.lastSubmissionStatus
                                          )}
                                        </Badge>
                                      </div>
                                    </div>

                                    {submission?.submissionData
                                      ?.submission_type === "group" && (
                                      <div className="flex items-center gap-2 mt-4">
                                        <Contact2 className="h-4 w-4 text-bits-grey-600" />
                                        <div className="flex items-center gap-2 whitespace-nowrap">
                                          <span className="text-bits-grey-600 text-sm font-normal leading-tight">
                                            Teammates:
                                          </span>
                                          <span className="text-bits-grey-600 text-sm font-normal leading-tight">
                                            {(() =>
                                              (
                                                submission?.submissionData
                                                  ?.group?.members ?? []
                                              )
                                                .filter(
                                                  (m: any) =>
                                                    m?.id !==
                                                    submission?.submissionData
                                                      ?.user?.id,
                                                )
                                                .map((m: any) => m?.name)
                                                .join(", ") || "-")()}
                                          </span>
                                        </div>
                                      </div>
                                    )}

                                    <div className="flex flex-row items-center justify-between mt-2">
                                      <div>
                                        <div className="flex flex-row items-center  gap-4">
                                          {submission?.submissionData
                                            ?.submission_type && (
                                            <div className="flex flex-row items-center gap-2">
                                              {submission?.submissionData
                                                ?.submission_type ===
                                              "group" ? (
                                                <Users className="h-4 w-4 text-bits-grey-600" />
                                              ) : (
                                                <User className="h-4 w-4 text-bits-grey-600" />
                                              )}
                                              <span className="text-bits-grey-600 text-sm font-normal">
                                                {
                                                  submission?.submissionData
                                                    ?.submission_type
                                                }
                                              </span>
                                            </div>
                                          )}
                                          <div className="flex flex-row items-center gap-2">
                                            <Clock className="h-4 w-4 text-bits-grey-600" />
                                            <span className="text-bits-grey-600 text-sm font-normal">
                                              {formatMaybeDate(
                                                submission.lastSubmissionDate,
                                              )}
                                            </span>
                                          </div>
                                          <div className="flex flex-row items-center  gap-2">
                                            <Flag className="ml-6 h-4 w-4 text-muted-foreground" />
                                            <div className="text-bits-grey-600 text-sm font-normal">
                                              {submission.lastSubmittedCheckpointName ||
                                                "-"}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-between text-sm font-semibold text-primary-foreground">
                                        <Button
                                          className="bg-bits-blue hover:bg-blue-700"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCurrentSubmissionId(
                                              submission.lastSubmissionId,
                                            );
                                            setIsFeedbackDialogOpen(true);
                                          }}
                                        >
                                          <Award className="h-4 w-4 mr-2" />
                                          <span className="text-sm font-semibold">
                                            Grade
                                          </span>
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                              {filteredSubmissions.length === 0 && (
                                <div className="text-center py-12 text-gray-500">
                                  <p className="text-lg mb-2">
                                    No submissions found
                                  </p>
                                  <p className="text-sm">
                                    {searchTerm ||
                                    statusFilter !== "all" ||
                                    checkpointsFilter !== "all"
                                      ? "Try adjusting your filters"
                                      : "Student submissions will appear here"}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    <div className="flex-1 flex-shrink-0">
                      <Card className="h-[calc(100vh-300px)] overflow-hidden">
                        <RightSubmissionPanel
                          key={`${selectedStudents?.[0]?.userId || "empty"}-${refreshKey}`}
                          members={selectedStudents}
                          projectId={projectId!}
                          maxAttempts={project?.max_attempts}
                        />
                      </Card>
                    </div>
                  </div>
                </div>
              </div>

              <FeedbackDialog
                isOpen={isFeedbackDialogOpen}
                onClose={() => {
                  setIsFeedbackDialogOpen(false);
                  queryClient.invalidateQueries({
                    queryKey: [projectId],
                  });
                  if (urlUserId) {
                    queryClient.invalidateQueries({
                      queryKey: [
                        "studentSubmissionDetails",
                        projectId,
                        urlUserId,
                      ],
                    });
                  }
                  // Force remount on close to ensure UI consistency
                  setRefreshKey((prev) => prev + 1);
                }}
                submissionId={currentSubmissionId}
                onSubmit={handleFeedbackSubmit}
              />
            </div>
          )}

          {activeTab === "students" && (
            <div>
              <div className="flex flex-row ml-auto p-2 w-full">
                <div>
                  <span className="text-lg font-medium text-bits-grey">
                    Students
                  </span>
                </div>
                <div className="flex flex-1 flex-row justify-end items-center mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-bits-grey-600" />
                    <Input
                      placeholder="Search Students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 w-50 text-md font-normal text-bits-grey-600 mr-2"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleExportStudentDetail()}
                  >
                    <Download className="h-4 w-4 text-bits-grey" />
                    <span className="text-sm font-semibold text-bits-grey">
                      Export
                    </span>
                  </Button>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="mt-6 ml-6 text-sm font-medium text-bits-grey">
                    <TableHead>Student</TableHead>
                    <TableHead>Email</TableHead>
                    {project?.type === "group" && <TableHead>Group</TableHead>}
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    {project?.publicationStatus === "draft" && (
                      <TableHead>Action</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // For group projects, flatten the group members into individual student rows
                    let studentsList: StudentRow[] = [];

                    if (
                      project?.type === "group" &&
                      project?.students?.[0]?.members
                    ) {
                      // Group project structure - extract members from groups
                      project.students.forEach((group) => {
                        if (group.members && Array.isArray(group.members)) {
                          group.members.forEach((member) => {
                            studentsList.push({
                              id: member.id,
                              name: member.name,
                              email: member.email,
                              status: member.status || group.status || "active",
                              groupName: group.groupName,
                              groupCode: group.groupCode,
                              groupId: group.groupId,
                            });
                          });
                        }
                      });
                    } else {
                      // Individual project or simple student structure
                      studentsList = (project?.students || []).map(
                        (student) => ({
                          id: student.id,
                          name: student.name,
                          email: student.email,
                          status: student.status,
                          groupName: undefined,
                          groupCode: undefined,
                          groupId: undefined,
                        }),
                      );
                    }
                    return studentsList
                      .filter((student: StudentRow) => {
                        // Filter out inactive students
                        const isActive =
                          student.status?.toLowerCase() === "active";

                        // Apply search filter
                        const matchesSearch = searchTerm
                          ? (student.name || "")
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            (student.email || "")
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()) ||
                            (student.groupName || "")
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          : true;

                        return isActive && matchesSearch;
                      })
                      .map((student: StudentRow) => {
                        const progress = calculateStudentProgress(student.id);

                        return (
                          <TableRow
                            key={`${student.id}-${student.groupId || ""}`}
                          >
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="text-sm font-medium text-bits-grey">
                                  {student.name || "N/A"}
                                </span>
                                {/* <span className="text-sm font-normal text-bits-grey-600">
                                  
                                </span> */}
                              </div>
                            </TableCell>
                            <TableCell className="text-md font-normal text-bits-grey">
                              {student.email || "N/A"}
                              {/* {student?.lms_user_id || "N/A"} */}
                            </TableCell>
                            {project?.type === "group" && (
                              <TableCell>
                                {student.groupName ? (
                                  <div className="flex flex-col">
                                    <span className="text-sm font-medium text-bits-grey">
                                      {student.groupName}
                                    </span>
                                    <span className="text-xs text-bits-grey-600">
                                      {student.groupCode}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-sm text-bits-grey-600 italic">
                                    No group assigned
                                  </span>
                                )}
                              </TableCell>
                            )}
                            <TableCell>
                              <Badge className="bg-bits-completed capitalize text-bits-green text-xs font-medium">
                                {student.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {progress.totalCheckpoints > 0 ? (
                                <div>
                                  <div className="flex justify-between text-xs font-normal text-bits-grey-600 mb-2">
                                    <span>
                                      {progress.completedCheckpoints}/
                                      {progress.totalCheckpoints} checkpoint
                                      {progress.totalCheckpoints !== 1
                                        ? "s"
                                        : ""}{" "}
                                      graded
                                    </span>
                                    <span className="font-medium">
                                      {progress.percentage}%
                                    </span>
                                  </div>
                                  <Progress
                                    value={progress.percentage}
                                    className="h-2"
                                  />
                                </div>
                              ) : (
                                <div className="text-bits-grey-600 text-sm">
                                  No checkpoints defined
                                </div>
                              )}
                            </TableCell>

                            {project?.publicationStatus === "draft" && (
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteStudent(
                                      student.id,
                                      student.name || "this student",
                                    );
                                  }}
                                  disabled={deleteUserMutation.isPending}
                                  className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      });
                  })()}
                </TableBody>
              </Table>

              {project?.students?.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg mb-2">No students enrolled</p>
                  <p className="text-sm">
                    Students will appear here once they are assigned to this
                    project
                  </p>
                </div>
              )}
            </div>
          )}
          <ImportGroupDialog
            isOpen={isImportGroupDialogOpen}
            onClose={() => setIsImportGroupDialogOpen(false)}
            projectId={projectId!}
            apiProject={apiProject}
            onImportSuccess={async () => {
              await queryClient.invalidateQueries({
                queryKey: ["project", projectId],
              });

              await queryClient.refetchQueries({
                queryKey: ["project", projectId],
              });
            }}
            onImportError={(error: any) => {
              const errorMessage = getErrorMessage(
                error,
                "Failed to import groups",
              );
              toast.error(errorMessage);
            }}
          />
          {activeTab === "evaluation" && (
            <div className="flex flex-col gap-6">
              <Card className="w-full">
                <CardHeader className="flex flex-row items-center justify-between pb-2 mb-4 border-b">
                  <div className="flex items-center space-x-2">
                    <File className="h-6 w-6 text-bits-blue" />
                    <CardTitle className="text-xl font-medium text-bits-blue-800">
                      AI Generated Python Test Script
                    </CardTitle>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() =>
                        setConfirmModalState({ isOpen: true, type: "generate" })
                      }
                      disabled={
                        pollingData?.status === "generating" ||
                        generateScriptMutation.isPending ||
                        updateScriptMutation.isPending
                      }
                      className="bg-bits-blue hover:bg-blue-700 text-white"
                    >
                      {pollingData?.status === "generating" ||
                      generateScriptMutation.isPending
                        ? "Generating..."
                        : "Generate AI Script"}
                    </Button>
                    <Button
                      onClick={() =>
                        setConfirmModalState({ isOpen: true, type: "save" })
                      }
                      disabled={
                        pollingData?.status === "generating" ||
                        updateScriptMutation.isPending ||
                        generateScriptMutation.isPending ||
                        localScriptContent === null ||
                        localScriptContent === evaluationScript
                      }
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {updateScriptMutation.isPending
                        ? "Saving..."
                        : "Save Script"}
                    </Button>
                    <Button
                      onClick={() => {
                        if (evaluationScript) {
                          const blobScript =
                            typeof evaluationScript === "string"
                              ? evaluationScript
                              : "";
                          const blob = new Blob([blobScript], {
                            type: "text/x-python",
                          });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          const sanitizedTitle =
                            project?.title
                              ?.replace(/[^a-z0-9]/gi, "_")
                              .toLowerCase() || "project";
                          a.download = `evaluation_script_${sanitizedTitle}.py`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                        }
                      }}
                      disabled={
                        pollingData?.status === "generating" ||
                        !evaluationScript ||
                        scriptLoading
                      }
                      variant="outline"
                      className="border-bits-blue text-bits-blue hover:text-white hover:bg-bits-blue"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-sm text-bits-grey-600">
                      The active Python evaluation script used for Auto-Grading
                      is displayed below. This script is generated contextually
                      based on the Checkpoints & Rubrics configured during
                      project creation. Clicking Update Test Script will
                      asynchronously re-poll Turf-AI for the latest generation.
                    </p>
                  </div>

                  <div className="relative rounded-md overflow-hidden bg-gray-900 border border-gray-700 min-h-[400px]">
                    {scriptLoading ? (
                      <div className="flex justify-center items-center h-64 h-full text-gray-400">
                        Loading Evaluation Script from S3...
                      </div>
                    ) : scriptError ? (
                      <div className="flex justify-center items-center h-64 h-full text-red-400 text-sm">
                        {(scriptError as any)?.response?.data?.message ||
                          "Test script not available or missing in S3 hook."}
                      </div>
                    ) : evaluationScript || localScriptContent !== null ? (
                      <div
                        className={cn(
                          "relative transition-all duration-300 rounded-lg overflow-hidden border border-gray-700",
                          isFullScreen
                            ? "fixed inset-0 z-50 bg-[#282c34] flex flex-col p-4 w-screen h-screen"
                            : "h-[600px] w-full flex flex-col",
                        )}
                      >
                        {/* Editor Top Bar Toolbar */}
                        <div className="flex justify-between items-center bg-[#21252b] p-2 border-b border-gray-700 h-12 flex-shrink-0">
                          <span className="text-gray-300 font-mono text-sm pl-2">
                            evaluation_script.py{" "}
                            {isEditingScript ? "(Editing)" : "(Read-Only)"}
                          </span>
                          <div className="flex gap-3 pr-2">
                            <button
                              onClick={() =>
                                setIsEditingScript(!isEditingScript)
                              }
                              title={
                                isEditingScript
                                  ? "Lock Editor"
                                  : "Unlock Editor"
                              }
                              className="text-gray-400 hover:text-white transition"
                            >
                              <Edit2
                                size={18}
                                className={
                                  isEditingScript ? "text-blue-400" : ""
                                }
                              />
                            </button>
                            <button
                              onClick={() => setIsFullScreen(!isFullScreen)}
                              title={
                                isFullScreen ? "Exit Fullscreen" : "Fullscreen"
                              }
                              className="text-gray-400 hover:text-white transition"
                            >
                              {isFullScreen ? (
                                <Minimize2 size={18} />
                              ) : (
                                <Maximize2 size={18} />
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 overflow-auto bg-[#282c34]">
                          <CodeMirror
                            value={localScriptContent || ""}
                            theme={oneDark}
                            extensions={[python()]}
                            readOnly={!isEditingScript}
                            onChange={(value) => setLocalScriptContent(value)}
                            className={cn(
                              "border-none !h-full [&>.cm-editor]:h-full",
                              isEditingScript ? "" : "opacity-80",
                            )}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-center flex-col gap-2 items-center h-64 h-full text-gray-400">
                        <span>No Evaluation Script Attached.</span>
                        <span className="text-xs">
                          Click Generate AI Script to attempt generation.
                        </span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Confirmation Modals for Script Evaluation Tab */}
          <AlertDialog
            open={confirmModalState.isOpen}
            onOpenChange={(isOpen) => {
              if (!isOpen) setConfirmModalState({ isOpen: false, type: null });
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirmModalState.type === "generate"
                    ? "Generate AI Script"
                    : "Save Custom Script"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirmModalState.type === "generate"
                    ? "This will instruct Turf-AI to re-evaluate the project parameters and generate a new Python testing script from scratch. This may take a minute. Are you sure you wish to proceed?"
                    : "This will overwrite the current AI script attached to this project with your manually edited version. Are you sure you wish to save this script to S3?"}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel
                  disabled={
                    generateScriptMutation.isPending ||
                    updateScriptMutation.isPending
                  }
                >
                  Cancel
                </AlertDialogCancel>
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    if (projectId) {
                      if (confirmModalState.type === "generate") {
                        generateScriptMutation.mutate(projectId, {
                          onSettled: () =>
                            setConfirmModalState({ isOpen: false, type: null }),
                        });
                      } else if (
                        confirmModalState.type === "save" &&
                        localScriptContent !== null
                      ) {
                        updateScriptMutation.mutate(
                          { id: projectId, scriptContent: localScriptContent },
                          {
                            onSettled: () =>
                              setConfirmModalState({
                                isOpen: false,
                                type: null,
                              }),
                          },
                        );
                      }
                    }
                  }}
                  disabled={
                    generateScriptMutation.isPending ||
                    updateScriptMutation.isPending
                  }
                  className="bg-bits-blue text-white hover:bg-blue-700"
                >
                  {generateScriptMutation.isPending ||
                  updateScriptMutation.isPending
                    ? "Processing..."
                    : "Confirm"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
